import EditIcon from "@atlaskit/icon/core/edit";
import GrowDiagonalIcon from "@atlaskit/icon/core/grow-diagonal";
import ShowMoreHorizontalIcon from "@atlaskit/icon/core/show-more-horizontal";
import ShrinkDiagonalIcon from "@atlaskit/icon/core/shrink-diagonal";
import {Modal} from "@mantine/core";
import {useMediaQuery} from "@mantine/hooks";
import {useRef, type Dispatch, type MouseEvent, type ReactNode, type SetStateAction} from "react";
import {AnankeTempDropdownMenuWithIconButtonTrigger} from "~/components/anankeTemp/anankeTempDropdownMenuWithIconButtonTrigger";
import {AnankeTempPageTitle} from "~/components/anankeTemp/anankeTempPageTitle";
import {Breadcrumbs} from "~/components/breadcrumbs";
import {StandardModalHeader, StandardModalHeaderDivider} from "~/components/standardModalHeader";
import {AsidePanelPortal} from "~/components/ottopilotDataViewer/asidePanelPortal";
import type {OttopilotDataViewerController} from "~/components/ottopilotDataViewer/ottopilotDataViewerController";
import {
    RowEngagementUnsavedChangesProvider,
    useGuardedPanelDismiss,
} from "~/components/ottopilotDataViewer/rowEngagement/rowEngagementUnsavedChangesContext";
import {AnankeButton} from "@vesta/ananke/components/anankeButton";
import {AnankeButtonGroup} from "@vesta/ananke/components/anankeButtonGroup";
import {AnankeDropdownItem} from "@vesta/ananke/components/anankeDropdownItem";
import {AnankeDropdownItemGroup} from "@vesta/ananke/components/anankeDropdownItemGroup";
import {AnankeHeading} from "@vesta/ananke/components/anankeHeading";
import {AnankeIconButton} from "@vesta/ananke/components/anankeIconButton";
import type {Uuid} from "@vesta/common-type-definitions/typeDefinitions";
import {VerticalSpacer} from "@vesta/global-common-typescript/components/verticalSpacer";

export type ReadPanelLayout = "profile" | "tabs" | "entityProfile";

// TODO: Figure out a better name for this
export type CurrentlyOpenRowEngagementPanel<T extends {id: Uuid}> =
    | {
          rowEngagementType: RowEngagementType.none;
          rowEngagementPanelType: RowEngagementPanelType.none;
          rowEngagementData: null;
      }
    | {
          rowEngagementType: RowEngagementType.create;
          rowEngagementPanelType: RowEngagementPanelType.fullscreen | RowEngagementPanelType.contentArea;
          rowEngagementData: null;
      }
    | {
          rowEngagementType: Exclude<RowEngagementType, RowEngagementType.none | RowEngagementType.create>;
          rowEngagementPanelType: Exclude<RowEngagementPanelType, RowEngagementPanelType.none>;
          rowEngagementData: T;
          readPanelLayout?: ReadPanelLayout;
          /** When `usePanelTabs` is true: 0 = Details, 1 = Activity, 2 = Notes. */
          readPanelTabIndex?: number;
          /** Hides sidebar/fullscreen grow-shrink header control (e.g. day-plan focus "view all"). */
          hidePanelLayoutToggle?: boolean;
      };

export enum RowEngagementPanelType {
    fullscreen = "fbef5772-621b-43e4-83e8-ffda85a18bf0",
    none = "b4292200-039d-4574-809c-3e9f222e34fd",
    sidebar = "3ecbbcac-68f1-473b-91fd-011222b29086",
    /** Renders inside the content area only; does not cover sidebar or page header */
    contentArea = "a7c8e9d0-1b2c-4e5f-8a9b-0c1d2e3f4a5b",
}

export enum RowEngagementType {
    create = "a4bfd72d-69ba-42fe-8471-91fd74ae686c",
    // delete = "68f4f3d2-28c5-47e6-bc80-0672f39865bb",
    none = "6c02038e-4442-415c-a720-18ed6ad1b94a",
    read = "e8a26290-ccc6-4b5a-bb76-a0646afc236f",
    update = "778b05ef-3d16-4dd0-8873-26952b5471db",
}

const MOBILE_ASIDE_BREAKPOINT = "(max-width: 47.9375em)";

function PanelModalHeaderRow({
    title,
    headerActions,
    onCloseHeader,
    closeDisabled,
    headingId,
    useStandardCloseButton = false,
}: {
    title: ReactNode;
    headerActions?: ReactNode;
    onCloseHeader?: () => void;
    closeDisabled?: boolean;
    headingId?: string;
    useStandardCloseButton?: boolean;
}) {
    if (useStandardCloseButton === true && onCloseHeader !== undefined) {
        return (
            <>
                <StandardModalHeader
                    title={title}
                    onClose={onCloseHeader}
                    headerActions={headerActions}
                    closeDisabled={closeDisabled}
                    headingId={headingId}
                />
                <StandardModalHeaderDivider />
            </>
        );
    }

    return (
        <>
            <div className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_max-content] items-center gap-ds-space-200 px-ds-space-250 py-ds-space-150">
                <div
                    id={headingId}
                    className={typeof title === "string" ? "*:line-clamp-1" : "min-w-0"}
                >
                    {typeof title === "string" ? (
                        <AnankeHeading size="small">{title}</AnankeHeading>
                    ) : (
                        title
                    )}
                </div>
                <AnankeButtonGroup>{headerActions}</AnankeButtonGroup>
            </div>
            <StandardModalHeaderDivider />
        </>
    );
}

function rowEngagementPanelAllowsFullscreenToggle<T extends {id: Uuid}>(
    controller: OttopilotDataViewerController<T>,
    currentlyOpenRowEngagementPanel: CurrentlyOpenRowEngagementPanel<T>,
): boolean {
    if (controller.rowEngagementPanelAllowsFullscreenToggle === false) {
        return false;
    }

    if (
        currentlyOpenRowEngagementPanel.rowEngagementType !== RowEngagementType.none &&
        currentlyOpenRowEngagementPanel.rowEngagementType !== RowEngagementType.create &&
        currentlyOpenRowEngagementPanel.hidePanelLayoutToggle === true
    ) {
        return false;
    }

    return true;
}

function withPanelType<T extends {id: Uuid}>(
    prev: CurrentlyOpenRowEngagementPanel<T>,
    rowEngagementPanelType:
        | RowEngagementPanelType.fullscreen
        | RowEngagementPanelType.sidebar
        | RowEngagementPanelType.contentArea,
): CurrentlyOpenRowEngagementPanel<T> {
    if (prev.rowEngagementType === RowEngagementType.none) {
        return prev;
    }
    if (prev.rowEngagementType === RowEngagementType.create) {
        return {
            rowEngagementType: RowEngagementType.create,
            rowEngagementPanelType:
                rowEngagementPanelType === RowEngagementPanelType.sidebar
                    ? RowEngagementPanelType.fullscreen
                    : rowEngagementPanelType,
            rowEngagementData: null,
        };
    }
    return {
        ...prev,
        rowEngagementPanelType,
    };
}

export function withEngagementTypeUpdate<T extends {id: Uuid}>(
    prev: CurrentlyOpenRowEngagementPanel<T>,
): CurrentlyOpenRowEngagementPanel<T> {
    if (prev.rowEngagementType === RowEngagementType.none || prev.rowEngagementType === RowEngagementType.create) {
        return prev;
    }
    return {
        ...prev,
        rowEngagementType: RowEngagementType.update,
    };
}

function resolveReadPanelHeaderLeft<T extends {id: Uuid}>({
    controller,
    currentlyOpenRowEngagementPanel,
}: {
    controller: OttopilotDataViewerController<T>;
    currentlyOpenRowEngagementPanel: CurrentlyOpenRowEngagementPanel<T>;
}): string {
    const customHeader = controller.resolveReadPanelHeaderLeft?.({
        row: currentlyOpenRowEngagementPanel.rowEngagementData!,
        readPanelLayout:
            currentlyOpenRowEngagementPanel.rowEngagementType === RowEngagementType.read
                ? currentlyOpenRowEngagementPanel.readPanelLayout
                : undefined,
    });

    if (customHeader !== undefined) {
        return customHeader;
    }

    return `View ${controller.rowDisplayNameVariants.singularLowerCase}`;
}

function resolveReadPanelFooter<T extends {id: Uuid}>({
    controller,
    row,
    canEdit,
    setCurrentlyOpenRowEngagementPanel,
}: {
    controller: OttopilotDataViewerController<T>;
    row: T;
    canEdit: boolean;
    setCurrentlyOpenRowEngagementPanel: Dispatch<SetStateAction<CurrentlyOpenRowEngagementPanel<T>>>;
}): ReactNode {
    if (controller.readPanelFooterComponent !== undefined) {
        return controller.readPanelFooterComponent(row);
    }
    if (controller.hideEditPanelFooter === true) {
        return null;
    }
    return (
        <div className="flex h-full w-full min-w-0 items-center justify-end px-ds-space-150">
            <AnankeButton
                type="button"
                appearance="primary"
                iconBefore={(props) => <EditIcon {...props} />}
                isDisabled={canEdit !== true}
                onClick={() => {
                    if (canEdit !== true) {
                        return;
                    }
                    setCurrentlyOpenRowEngagementPanel((prev) => {
                        const updated = withEngagementTypeUpdate(prev);
                        return controller.updatePanelOpensInFullscreen === true
                            ? withPanelType(updated, RowEngagementPanelType.fullscreen)
                            : updated;
                    });
                }}
            >
                Edit
            </AnankeButton>
        </div>
    );
}

function wrapPanelContent(
    content: ReactNode,
    contentWrapper: ((content: ReactNode) => ReactNode) | undefined,
): ReactNode {
    if (contentWrapper === undefined) {
        return content;
    }
    return contentWrapper(content);
}

/** Matches TableFooter page variant height so the aside separator aligns with the table footer. */
function RowEngagementPanelFooterSlot({children}: {children: ReactNode}) {
    return (
        <>
            <div className="w-full h-[var(--ds-border-width)] shrink-0 bg-ds-border" />
            <div className="box-border flex h-[58px] min-h-[58px] w-full shrink-0 items-center bg-ds-surface-raised">
                {children}
            </div>
        </>
    );
}

export function RowEngagementPanel<T extends {id: Uuid}>({
    controller,
}: {
    controller: OttopilotDataViewerController<T>;
}) {
    const currentlyOpenRowEngagementPanel = controller.currentlyOpenRowEngagementPanel;
    const setCurrentlyOpenRowEngagementPanel = controller.setCurrentlyOpenRowEngagementPanel;

    const panel = ((): ReactNode => {
        switch (currentlyOpenRowEngagementPanel.rowEngagementType) {
            case RowEngagementType.create: {
                return (
                    <RowEngagementPanel_Create
                        controller={controller}
                        currentlyOpenRowEngagementPanel={currentlyOpenRowEngagementPanel}
                        setCurrentlyOpenRowEngagementPanel={setCurrentlyOpenRowEngagementPanel}
                    />
                );
            }
            case RowEngagementType.read: {
                return (
                    <RowEngagementPanel_Read
                        controller={controller}
                        currentlyOpenRowEngagementPanel={currentlyOpenRowEngagementPanel}
                        setCurrentlyOpenRowEngagementPanel={setCurrentlyOpenRowEngagementPanel}
                    />
                );
            }
            case RowEngagementType.none: {
                return null;
            }
            case RowEngagementType.update: {
                return (
                    <RowEngagementPanel_Update
                        controller={controller}
                        currentlyOpenRowEngagementPanel={currentlyOpenRowEngagementPanel}
                        setCurrentlyOpenRowEngagementPanel={setCurrentlyOpenRowEngagementPanel}
                    />
                );
            }
            default: {
                console.log("a1a8932e-50ce-4d28-9c63-24cb512fac24", currentlyOpenRowEngagementPanel);
                return null;
            }
        }
    })();

    return <RowEngagementUnsavedChangesProvider>{panel}</RowEngagementUnsavedChangesProvider>;
}

/** Builds the "close panel" state update shared by every panel variant. */
function closeRowEngagementPanel<T extends {id: Uuid}>(
    setCurrentlyOpenRowEngagementPanel: Dispatch<SetStateAction<CurrentlyOpenRowEngagementPanel<T>>>,
): void {
    setCurrentlyOpenRowEngagementPanel({
        rowEngagementType: RowEngagementType.none,
        rowEngagementPanelType: RowEngagementPanelType.none,
        rowEngagementData: null,
    });
}

function RowEngagementPanel_Create<T extends {id: Uuid}>({
    controller,
    currentlyOpenRowEngagementPanel,
    setCurrentlyOpenRowEngagementPanel,
}: {
    controller: OttopilotDataViewerController<T>;
    currentlyOpenRowEngagementPanel: CurrentlyOpenRowEngagementPanel<T>;
    setCurrentlyOpenRowEngagementPanel: Dispatch<SetStateAction<CurrentlyOpenRowEngagementPanel<T>>>;
}) {
    switch (currentlyOpenRowEngagementPanel.rowEngagementPanelType) {
        case RowEngagementPanelType.fullscreen: {
            return (
                <RowEngagementPanel_Create_Fullscreen
                    controller={controller}
                    setCurrentlyOpenRowEngagementPanel={setCurrentlyOpenRowEngagementPanel}
                />
            );
        }
        case RowEngagementPanelType.none: {
            return null;
        }
        case RowEngagementPanelType.sidebar: {
            return (
                <RowEngagementPanel_Create_Sidebar
                    controller={controller}
                    setCurrentlyOpenRowEngagementPanel={setCurrentlyOpenRowEngagementPanel}
                />
            );
        }
        case RowEngagementPanelType.contentArea: {
            return (
                <RowEngagementPanel_Create_ContentArea
                    controller={controller}
                    setCurrentlyOpenRowEngagementPanel={setCurrentlyOpenRowEngagementPanel}
                />
            );
        }
        default: {
            console.log("7b5ceb79-41ab-451e-9810-c6e41768dbac", currentlyOpenRowEngagementPanel);
            return null;
        }
    }
}

function RowEngagementPanel_Read<T extends {id: Uuid}>({
    controller,
    currentlyOpenRowEngagementPanel,
    setCurrentlyOpenRowEngagementPanel,
}: {
    controller: OttopilotDataViewerController<T>;
    currentlyOpenRowEngagementPanel: CurrentlyOpenRowEngagementPanel<T>;
    setCurrentlyOpenRowEngagementPanel: Dispatch<SetStateAction<CurrentlyOpenRowEngagementPanel<T>>>;
}) {
    switch (currentlyOpenRowEngagementPanel.rowEngagementPanelType) {
        case RowEngagementPanelType.fullscreen: {
            return (
                <RowEngagementPanel_Read_Fullscreen
                    controller={controller}
                    currentlyOpenRowEngagementPanel={currentlyOpenRowEngagementPanel}
                    setCurrentlyOpenRowEngagementPanel={setCurrentlyOpenRowEngagementPanel}
                />
            );
        }
        case RowEngagementPanelType.none: {
            return null;
        }
        case RowEngagementPanelType.sidebar: {
            return (
                <RowEngagementPanel_Read_Sidebar
                    controller={controller}
                    currentlyOpenRowEngagementPanel={currentlyOpenRowEngagementPanel}
                    setCurrentlyOpenRowEngagementPanel={setCurrentlyOpenRowEngagementPanel}
                />
            );
        }
        case RowEngagementPanelType.contentArea: {
            return null;
        }
        default: {
            console.log("64f50f71-daf3-4e2d-9aef-23586d78d700", currentlyOpenRowEngagementPanel);
            return null;
        }
    }
}

function RowEngagementPanel_Update<T extends {id: Uuid}>({
    controller,
    currentlyOpenRowEngagementPanel,
    setCurrentlyOpenRowEngagementPanel,
}: {
    controller: OttopilotDataViewerController<T>;
    currentlyOpenRowEngagementPanel: CurrentlyOpenRowEngagementPanel<T>;
    setCurrentlyOpenRowEngagementPanel: Dispatch<SetStateAction<CurrentlyOpenRowEngagementPanel<T>>>;
}) {
    switch (currentlyOpenRowEngagementPanel.rowEngagementPanelType) {
        case RowEngagementPanelType.fullscreen: {
            return (
                <RowEngagementPanel_Update_Fullscreen
                    controller={controller}
                    currentlyOpenRowEngagementPanel={currentlyOpenRowEngagementPanel}
                    setCurrentlyOpenRowEngagementPanel={setCurrentlyOpenRowEngagementPanel}
                />
            );
        }
        case RowEngagementPanelType.none: {
            return null;
        }
        case RowEngagementPanelType.sidebar: {
            return (
                <RowEngagementPanel_Update_Sidebar
                    controller={controller}
                    currentlyOpenRowEngagementPanel={currentlyOpenRowEngagementPanel}
                    setCurrentlyOpenRowEngagementPanel={setCurrentlyOpenRowEngagementPanel}
                />
            );
        }
        case RowEngagementPanelType.contentArea: {
            return null;
        }
        default: {
            console.log("6e2d41e7-0520-454f-adad-9a9dc9f5ebff", currentlyOpenRowEngagementPanel);
            return null;
        }
    }
}

function RowEngagementPanel_Create_Fullscreen<T extends {id: Uuid}>({
    controller,
    setCurrentlyOpenRowEngagementPanel,
}: {
    controller: OttopilotDataViewerController<T>;
    setCurrentlyOpenRowEngagementPanel: Dispatch<SetStateAction<CurrentlyOpenRowEngagementPanel<T>>>;
}) {
    const isMobile = useMediaQuery(MOBILE_ASIDE_BREAKPOINT);
    const requestDismiss = useGuardedPanelDismiss();
    const dismissPanel = () => requestDismiss(() => closeRowEngagementPanel(setCurrentlyOpenRowEngagementPanel));

    if (isMobile === true) {
        return (
            <CreateFullscreenModal
                headerLeft={`Create ${controller.rowDisplayNameVariants.singularSentenceCase}`}
                breadCrumbItems={controller.breadCrumbItems}
                body={controller.createPanelComponent?.(true)}
                onClose={() => {
                    dismissPanel();
                }}
            />
        );
    }

    return (
        <AsideFullscreenPanel
            headerLeft={`Create ${controller.rowDisplayNameVariants.singularSentenceCase}`}
            useStandardCloseButton={true}
            onCloseHeader={() => {
                dismissPanel();
            }}
            body={controller.createPanelComponent?.(true)}
        />
    );
}

function RowEngagementPanel_Create_ContentArea<T extends {id: Uuid}>({
    controller,
    setCurrentlyOpenRowEngagementPanel,
}: {
    controller: OttopilotDataViewerController<T>;
    setCurrentlyOpenRowEngagementPanel: Dispatch<SetStateAction<CurrentlyOpenRowEngagementPanel<T>>>;
}) {
    const requestDismiss = useGuardedPanelDismiss();
    const dismissPanel = () => requestDismiss(() => closeRowEngagementPanel(setCurrentlyOpenRowEngagementPanel));
    return (
        <ContentAreaModal
            headerLeft={`Create ${controller.rowDisplayNameVariants.singularSentenceCase}`}
            useStandardCloseButton={true}
            onCloseHeader={() => {
                dismissPanel();
            }}
            body={controller.createPanelComponent?.(true)}
        />
    );
}

function RowEngagementPanel_Create_Sidebar<T extends {id: Uuid}>({
    controller,
    setCurrentlyOpenRowEngagementPanel,
}: {
    controller: OttopilotDataViewerController<T>;
    setCurrentlyOpenRowEngagementPanel: Dispatch<SetStateAction<CurrentlyOpenRowEngagementPanel<T>>>;
}) {
    const isMobile = useMediaQuery(MOBILE_ASIDE_BREAKPOINT);
    const requestDismiss = useGuardedPanelDismiss();
    const dismissPanel = () => requestDismiss(() => closeRowEngagementPanel(setCurrentlyOpenRowEngagementPanel));

    const headerActions = (
        <div className="max-xs:hidden">
            <AnankeIconButton
                type="button"
                appearance="subtle"
                onClick={() => {
                    setCurrentlyOpenRowEngagementPanel((prev) =>
                        withPanelType(prev, RowEngagementPanelType.fullscreen),
                    );
                }}
                icon={GrowDiagonalIcon}
                label=""
            />
        </div>
    );

    if (isMobile === true) {
        return (
            <SidebarModal
                headerLeft={`Create ${controller.rowDisplayNameVariants.singularSentenceCase}`}
                headerActions={headerActions}
                useStandardCloseButton={true}
                onClose={() => {
                    dismissPanel();
                }}
                body={controller.createPanelComponent?.(false)}
            />
        );
    }

    return (
        <AsideSidebarPanel
            headerLeft={`Create ${controller.rowDisplayNameVariants.singularSentenceCase}`}
            headerActions={headerActions}
            useStandardCloseButton={true}
            onCloseHeader={() => {
                dismissPanel();
            }}
            body={controller.createPanelComponent?.(false)}
        />
    );
}

function RowEngagementPanel_Read_Fullscreen<T extends {id: Uuid}>({
    controller,
    currentlyOpenRowEngagementPanel,
    setCurrentlyOpenRowEngagementPanel,
}: {
    controller: OttopilotDataViewerController<T>;
    currentlyOpenRowEngagementPanel: CurrentlyOpenRowEngagementPanel<T>;
    setCurrentlyOpenRowEngagementPanel: Dispatch<SetStateAction<CurrentlyOpenRowEngagementPanel<T>>>;
}) {
    const isMobile = useMediaQuery(MOBILE_ASIDE_BREAKPOINT);
    const requestDismiss = useGuardedPanelDismiss();
    const dismissPanel = () => requestDismiss(() => closeRowEngagementPanel(setCurrentlyOpenRowEngagementPanel));
    const canEdit = controller.canEditRow?.(currentlyOpenRowEngagementPanel.rowEngagementData!) ?? true;

    if (currentlyOpenRowEngagementPanel.readPanelLayout === "entityProfile") {
        const body = controller.readPanelComponent?.(currentlyOpenRowEngagementPanel.rowEngagementData!, true);
        if (isMobile === true) {
            if (controller.mobileRowEngagementPanelInContentArea === true) {
                return <ContentAreaModal hideHeader={true} body={body} />;
            }
            return (
                <SidebarModal
                    headerLeft=""
                    hideHeader={true}
                    body={body}
                    onClose={() => {
                        dismissPanel();
                    }}
                />
            );
        }
        return (
            <AsidePanelPortal isFullscreen={true}>
                <div className="h-full min-h-0 min-w-0 w-full">{body}</div>
            </AsidePanelPortal>
        );
    }

    const headerActions = (
        <>
            <AnankeTempDropdownMenuWithIconButtonTrigger
                iconButtonProps={{
                    type: "button",
                    appearance: "subtle",
                    icon: ShowMoreHorizontalIcon,
                    label: "",
                }}
            >
                <AnankeDropdownItemGroup>
                    {controller.hideReadPanelEditMenuItem !== true &&
                        (controller.canEditRow?.(currentlyOpenRowEngagementPanel.rowEngagementData!) ?? true) === true && (
                            <AnankeDropdownItem
                                onClick={() => {
                                    setCurrentlyOpenRowEngagementPanel((prev) => {
                                        const updated = withEngagementTypeUpdate(prev);
                                        return controller.updatePanelOpensInFullscreen === true
                                            ? withPanelType(updated, RowEngagementPanelType.fullscreen)
                                            : updated;
                                    });
                                }}
                                elemBefore={<EditIcon label="" />}
                            >
                                Edit
                            </AnankeDropdownItem>
                        )}
                    {controller.rowEngagementDropdownOptions?.(currentlyOpenRowEngagementPanel.rowEngagementData!)}
                </AnankeDropdownItemGroup>
            </AnankeTempDropdownMenuWithIconButtonTrigger>

            <div className="max-xs:hidden">
                {rowEngagementPanelAllowsFullscreenToggle(controller, currentlyOpenRowEngagementPanel) === true ? (
                    <AnankeIconButton
                        type="button"
                        appearance="subtle"
                        onClick={() => {
                            setCurrentlyOpenRowEngagementPanel((prev) =>
                                withPanelType(prev, RowEngagementPanelType.sidebar),
                            );
                        }}
                        icon={ShrinkDiagonalIcon}
                        label=""
                    />
                ) : null}
            </div>
        </>
    );

    const footer = resolveReadPanelFooter({
        controller,
        row: currentlyOpenRowEngagementPanel.rowEngagementData!,
        canEdit,
        setCurrentlyOpenRowEngagementPanel,
    });
    const headerLeft = resolveReadPanelHeaderLeft({controller, currentlyOpenRowEngagementPanel});

    if (isMobile === true) {
        if (controller.mobileRowEngagementPanelInContentArea === true) {
            return (
                <ContentAreaModal
                    headerLeft={headerLeft}
                    headerActions={headerActions}
                    useStandardCloseButton={true}
                    onCloseHeader={() => {
                        dismissPanel();
                    }}
                    body={controller.readPanelComponent?.(currentlyOpenRowEngagementPanel.rowEngagementData!, true)}
                />
            );
        }
        return (
            <FullscreenModal
                headerLeft={headerLeft}
                headerActions={headerActions}
                useStandardCloseButton={true}
                body={controller.readPanelComponent?.(currentlyOpenRowEngagementPanel.rowEngagementData!, true)}
                footer={footer}
                onClose={() => {
                    dismissPanel();
                }}
            />
        );
    }

    return (
        <AsideFullscreenPanel
            headerLeft={headerLeft}
            headerActions={headerActions}
            useStandardCloseButton={true}
            onCloseHeader={() => {
                dismissPanel();
            }}
            body={controller.readPanelComponent?.(currentlyOpenRowEngagementPanel.rowEngagementData!, true)}
            footer={footer}
        />
    );
}

function RowEngagementPanel_Read_Sidebar<T extends {id: Uuid}>({
    controller,
    currentlyOpenRowEngagementPanel,
    setCurrentlyOpenRowEngagementPanel,
}: {
    controller: OttopilotDataViewerController<T>;
    currentlyOpenRowEngagementPanel: CurrentlyOpenRowEngagementPanel<T>;
    setCurrentlyOpenRowEngagementPanel: Dispatch<SetStateAction<CurrentlyOpenRowEngagementPanel<T>>>;
}) {
    const isMobile = useMediaQuery(MOBILE_ASIDE_BREAKPOINT);
    const requestDismiss = useGuardedPanelDismiss();
    const dismissPanel = () => requestDismiss(() => closeRowEngagementPanel(setCurrentlyOpenRowEngagementPanel));
    const canEdit = controller.canEditRow?.(currentlyOpenRowEngagementPanel.rowEngagementData!) ?? true;

    // Entity profile aside owns its chrome (header / expand / footer).
    if (currentlyOpenRowEngagementPanel.readPanelLayout === "entityProfile") {
        const body = controller.readPanelComponent?.(currentlyOpenRowEngagementPanel.rowEngagementData!, false);
        if (isMobile === true) {
            if (controller.mobileRowEngagementPanelInContentArea === true) {
                return (
                    <ContentAreaModal
                        hideHeader={true}
                        body={body}
                    />
                );
            }
            return (
                <SidebarModal
                    headerLeft=""
                    hideHeader={true}
                    body={body}
                    onClose={() => {
                        dismissPanel();
                    }}
                />
            );
        }
        return (
            <AsidePanelPortal isFullscreen={false}>
                <div className="h-full min-h-0 min-w-0 w-full overflow-hidden">{body}</div>
            </AsidePanelPortal>
        );
    }

    const headerActions = (
        <>
            <AnankeTempDropdownMenuWithIconButtonTrigger
                iconButtonProps={{
                    type: "button",
                    appearance: "subtle",
                    icon: ShowMoreHorizontalIcon,
                    label: "",
                }}
            >
                <AnankeDropdownItemGroup>
                    {controller.hideReadPanelEditMenuItem !== true &&
                        (controller.canEditRow?.(currentlyOpenRowEngagementPanel.rowEngagementData!) ?? true) === true && (
                            <AnankeDropdownItem
                                onClick={() => {
                                    setCurrentlyOpenRowEngagementPanel((prev) => {
                                        const updated = withEngagementTypeUpdate(prev);
                                        return controller.updatePanelOpensInFullscreen === true
                                            ? withPanelType(updated, RowEngagementPanelType.fullscreen)
                                            : updated;
                                    });
                                }}
                                elemBefore={<EditIcon label="" />}
                            >
                                Edit
                            </AnankeDropdownItem>
                        )}
                    {controller.rowEngagementDropdownOptions?.(currentlyOpenRowEngagementPanel.rowEngagementData!)}
                </AnankeDropdownItemGroup>
            </AnankeTempDropdownMenuWithIconButtonTrigger>

            <div className="max-xs:hidden">
                {rowEngagementPanelAllowsFullscreenToggle(controller, currentlyOpenRowEngagementPanel) === true ? (
                    <AnankeIconButton
                        type="button"
                        appearance="subtle"
                        onClick={() => {
                            setCurrentlyOpenRowEngagementPanel((prev) =>
                                withPanelType(prev, RowEngagementPanelType.fullscreen),
                            );
                        }}
                        icon={GrowDiagonalIcon}
                        label=""
                    />
                ) : null}
            </div>
        </>
    );

    const footer = resolveReadPanelFooter({
        controller,
        row: currentlyOpenRowEngagementPanel.rowEngagementData!,
        canEdit,
        setCurrentlyOpenRowEngagementPanel,
    });
    const headerLeft = resolveReadPanelHeaderLeft({controller, currentlyOpenRowEngagementPanel});

    if (isMobile === true) {
        if (controller.mobileRowEngagementPanelInContentArea === true) {
            return (
                <ContentAreaModal
                    headerLeft={headerLeft}
                    headerActions={headerActions}
                    useStandardCloseButton={true}
                    onCloseHeader={() => {
                        dismissPanel();
                    }}
                    body={controller.readPanelComponent?.(currentlyOpenRowEngagementPanel.rowEngagementData!, false)}
                />
            );
        }
        return (
            <SidebarModal
                headerLeft={headerLeft}
                headerActions={headerActions}
                useStandardCloseButton={true}
                body={controller.readPanelComponent?.(currentlyOpenRowEngagementPanel.rowEngagementData!, false)}
                footer={footer}
                onClose={() => {
                    dismissPanel();
                }}
            />
        );
    }

    return (
        <AsideSidebarPanel
            headerLeft={headerLeft}
            headerActions={headerActions}
            useStandardCloseButton={true}
            onCloseHeader={() => {
                dismissPanel();
            }}
            body={controller.readPanelComponent?.(currentlyOpenRowEngagementPanel.rowEngagementData!, false)}
            footer={footer}
        />
    );
}

function RowEngagementPanel_Update_Fullscreen<T extends {id: Uuid}>({
    controller,
    currentlyOpenRowEngagementPanel,
    setCurrentlyOpenRowEngagementPanel,
}: {
    controller: OttopilotDataViewerController<T>;
    currentlyOpenRowEngagementPanel: CurrentlyOpenRowEngagementPanel<T>;
    setCurrentlyOpenRowEngagementPanel: Dispatch<SetStateAction<CurrentlyOpenRowEngagementPanel<T>>>;
}) {
    const isMobile = useMediaQuery(MOBILE_ASIDE_BREAKPOINT);
    const requestDismiss = useGuardedPanelDismiss();
    const dismissPanel = () => requestDismiss(() => closeRowEngagementPanel(setCurrentlyOpenRowEngagementPanel));

    const headerActions = (
        <>
            <div className="max-xs:hidden">
                {rowEngagementPanelAllowsFullscreenToggle(controller, currentlyOpenRowEngagementPanel) === true ? (
                    <AnankeIconButton
                        type="button"
                        appearance="subtle"
                        onClick={() => {
                            setCurrentlyOpenRowEngagementPanel((prev) =>
                                withPanelType(withEngagementTypeUpdate(prev), RowEngagementPanelType.sidebar),
                            );
                        }}
                        icon={ShrinkDiagonalIcon}
                        label=""
                    />
                ) : null}
            </div>
        </>
    );

    if (isMobile === true) {
        if (controller.mobileRowEngagementPanelInContentArea === true) {
            return (
                <ContentAreaModal
                    headerLeft={`Edit ${controller.rowDisplayNameVariants.singularLowerCase}`}
                    headerActions={headerActions}
                    useStandardCloseButton={true}
                    onCloseHeader={() => {
                        dismissPanel();
                    }}
                    body={controller.updatePanelComponent?.(currentlyOpenRowEngagementPanel.rowEngagementData!, true)}
                />
            );
        }
        return (
            <FullscreenModal
                headerLeft={`Edit ${controller.rowDisplayNameVariants.singularLowerCase}`}
                headerActions={headerActions}
                useStandardCloseButton={true}
                body={controller.updatePanelComponent?.(currentlyOpenRowEngagementPanel.rowEngagementData!, true)}
                bodyFillsViewport={true}
                onClose={() => {
                    dismissPanel();
                }}
            />
        );
    }

    return (
        <AsideFullscreenPanel
            headerLeft={`Edit ${controller.rowDisplayNameVariants.singularLowerCase}`}
            headerActions={headerActions}
            useStandardCloseButton={true}
            onCloseHeader={() => {
                dismissPanel();
            }}
            body={controller.updatePanelComponent?.(currentlyOpenRowEngagementPanel.rowEngagementData!, true)}
            bodyFillsViewport={true}
        />
    );
}

function RowEngagementPanel_Update_Sidebar<T extends {id: Uuid}>({
    controller,
    currentlyOpenRowEngagementPanel,
    setCurrentlyOpenRowEngagementPanel,
}: {
    controller: OttopilotDataViewerController<T>;
    currentlyOpenRowEngagementPanel: CurrentlyOpenRowEngagementPanel<T>;
    setCurrentlyOpenRowEngagementPanel: Dispatch<SetStateAction<CurrentlyOpenRowEngagementPanel<T>>>;
}) {
    const isMobile = useMediaQuery(MOBILE_ASIDE_BREAKPOINT);
    const requestDismiss = useGuardedPanelDismiss();
    const dismissPanel = () => requestDismiss(() => closeRowEngagementPanel(setCurrentlyOpenRowEngagementPanel));

    const headerActions = (
        <>
            <div className="max-xs:hidden">
                {rowEngagementPanelAllowsFullscreenToggle(controller, currentlyOpenRowEngagementPanel) === true ? (
                    <AnankeIconButton
                        type="button"
                        appearance="subtle"
                        onClick={() => {
                            setCurrentlyOpenRowEngagementPanel((prev) =>
                                withPanelType(prev, RowEngagementPanelType.fullscreen),
                            );
                        }}
                        icon={GrowDiagonalIcon}
                        label=""
                    />
                ) : null}
            </div>
        </>
    );

    if (isMobile === true) {
        if (controller.mobileRowEngagementPanelInContentArea === true) {
            return (
                <ContentAreaModal
                    headerLeft={`Edit ${controller.rowDisplayNameVariants.singularLowerCase}`}
                    headerActions={headerActions}
                    useStandardCloseButton={true}
                    onCloseHeader={() => {
                        dismissPanel();
                    }}
                    body={controller.updatePanelComponent?.(currentlyOpenRowEngagementPanel.rowEngagementData!, false)}
                />
            );
        }
        return (
            <SidebarModal
                headerLeft={`Edit ${controller.rowDisplayNameVariants.singularLowerCase}`}
                headerActions={headerActions}
                useStandardCloseButton={true}
                body={controller.updatePanelComponent?.(currentlyOpenRowEngagementPanel.rowEngagementData!, false)}
                onClose={() => {
                    dismissPanel();
                }}
            />
        );
    }

    return (
        <AsideSidebarPanel
            headerLeft={`Edit ${controller.rowDisplayNameVariants.singularLowerCase}`}
            headerActions={headerActions}
            useStandardCloseButton={true}
            onCloseHeader={() => {
                dismissPanel();
            }}
            body={controller.updatePanelComponent?.(currentlyOpenRowEngagementPanel.rowEngagementData!, false)}
        />
    );
}

/** Modal that fills only its positioning context (e.g. data viewer area), so sidebar and page header stay visible. */
export function ContentAreaModal({
    headerLeft,
    headerRight,
    headerActions,
    body,
    hideHeader = false,
    useStandardCloseButton = false,
    onCloseHeader,
    closeDisabled,
}: {
    headerLeft: ReactNode;
    /** @deprecated Pass `headerActions` and `useStandardCloseButton` instead. */
    headerRight?: ReactNode;
    headerActions?: ReactNode;
    body: ReactNode;
    /** When true, only the body is rendered (no title bar or close button). Use when the page header and sidebar are the only chrome. */
    hideHeader?: boolean;
    useStandardCloseButton?: boolean;
    onCloseHeader?: () => void;
    closeDisabled?: boolean;
}) {
    const resolvedHeaderActions = headerActions ?? headerRight;

    if (hideHeader) {
        return (
            <div
                className="absolute inset-0 z-50 bg-ds-elevation-surface-current"
                role="dialog"
                aria-modal="true"
            >
                <div className="relative h-full min-h-0 min-w-0 w-full">
                    <div className="absolute inset-0 min-w-0 overflow-x-hidden overflow-y-auto">{body}</div>
                </div>
            </div>
        );
    }
    return (
        <div
            className="absolute inset-0 z-50 grid grid-cols-1 grid-rows-[max-content_minmax(0,1fr)] bg-ds-elevation-surface-current"
            role="dialog"
            aria-modal="true"
            aria-labelledby="content-area-modal-heading"
        >
            <PanelModalHeaderRow
                title={headerLeft}
                headerActions={resolvedHeaderActions}
                onCloseHeader={onCloseHeader}
                closeDisabled={closeDisabled}
                useStandardCloseButton={useStandardCloseButton}
                headingId="content-area-modal-heading"
            />
            <div className="relative h-full min-h-0 min-w-0 w-full">
                <div className="absolute inset-0 min-w-0 overflow-x-hidden overflow-y-auto">{body}</div>
            </div>
        </div>
    );
}

export function AsideSidebarPanel({
    headerLeft,
    headerRight,
    headerActions,
    body,
    footer,
    bodyFillsViewport = false,
    useStandardCloseButton = false,
    onCloseHeader,
    closeDisabled,
    contentWrapper,
}: {
    headerLeft: ReactNode;
    /** @deprecated Pass `headerActions` and `useStandardCloseButton` instead. */
    headerRight?: ReactNode;
    headerActions?: ReactNode;
    body: ReactNode;
    footer?: ReactNode;
    bodyFillsViewport?: boolean;
    useStandardCloseButton?: boolean;
    onCloseHeader?: () => void;
    closeDisabled?: boolean;
    contentWrapper?: (content: ReactNode) => ReactNode;
}) {
    const resolvedHeaderActions = headerActions ?? headerRight;

    return (
        <AsidePanelPortal isFullscreen={false}>
            {wrapPanelContent(
                <div
                    className={
                        footer !== undefined && footer !== null
                            ? "grid h-full min-h-0 min-w-0 w-full grid-cols-1 grid-rows-[max-content_max-content_minmax(0,1fr)_max-content_max-content]"
                            : "grid h-full min-h-0 min-w-0 w-full grid-cols-1 grid-rows-[max-content_max-content_minmax(0,1fr)]"
                    }
                >
                    <PanelModalHeaderRow
                        title={headerLeft}
                        headerActions={resolvedHeaderActions}
                        onCloseHeader={onCloseHeader}
                        closeDisabled={closeDisabled}
                        useStandardCloseButton={useStandardCloseButton}
                    />
                    <div className="relative h-full min-h-0 min-w-0 w-full">
                        <div
                            className={
                                bodyFillsViewport === true
                                    ? "absolute inset-0 min-h-0 min-w-0 overflow-x-hidden overflow-y-hidden"
                                    : "absolute inset-0 min-w-0 overflow-x-hidden overflow-y-auto"
                            }
                        >
                            {body}
                        </div>
                    </div>
                    {footer !== undefined && footer !== null && (
                        <RowEngagementPanelFooterSlot>{footer}</RowEngagementPanelFooterSlot>
                    )}
                </div>,
                contentWrapper,
            )}
        </AsidePanelPortal>
    );
}

export function AsideFullscreenPanel({
    headerLeft,
    headerRight,
    headerActions,
    body,
    footer,
    /** When true, the body slot does not scroll; children should use min-h-0 flex + inner overflow (e.g. pinned footer). */
    bodyFillsViewport = false,
    useStandardCloseButton = false,
    onCloseHeader,
    closeDisabled,
    contentWrapper,
}: {
    headerLeft: ReactNode;
    /** @deprecated Pass `headerActions` and `useStandardCloseButton` instead. */
    headerRight?: ReactNode;
    headerActions?: ReactNode;
    body: ReactNode;
    footer?: ReactNode;
    bodyFillsViewport?: boolean;
    useStandardCloseButton?: boolean;
    onCloseHeader?: () => void;
    closeDisabled?: boolean;
    contentWrapper?: (content: ReactNode) => ReactNode;
}) {
    const resolvedHeaderActions = headerActions ?? headerRight;

    return (
        <AsidePanelPortal isFullscreen={true}>
            {wrapPanelContent(
                <div
                    className={
                        footer !== undefined && footer !== null
                            ? "grid h-full min-h-0 min-w-0 w-full grid-cols-1 grid-rows-[max-content_max-content_minmax(0,1fr)_max-content_max-content]"
                            : "grid h-full min-h-0 min-w-0 w-full grid-cols-1 grid-rows-[max-content_max-content_minmax(0,1fr)]"
                    }
                >
                    <PanelModalHeaderRow
                        title={headerLeft}
                        headerActions={resolvedHeaderActions}
                        onCloseHeader={onCloseHeader}
                        closeDisabled={closeDisabled}
                        useStandardCloseButton={useStandardCloseButton}
                    />
                    <div className="relative h-full min-h-0 min-w-0 w-full">
                        <div
                            className={
                                bodyFillsViewport === true
                                    ? "absolute inset-0 min-h-0 min-w-0 overflow-x-hidden overflow-y-hidden"
                                    : "absolute inset-0 min-w-0 overflow-x-hidden overflow-y-auto"
                            }
                        >
                            {body}
                        </div>
                    </div>
                    {footer !== undefined && footer !== null && (
                        <RowEngagementPanelFooterSlot>{footer}</RowEngagementPanelFooterSlot>
                    )}
                </div>,
                contentWrapper,
            )}
        </AsidePanelPortal>
    );
}

export function SidebarModal({
    headerLeft,
    headerRight,
    headerActions,
    body,
    footer,
    onClose,
    /** When true, body fills the panel height without an outer scroll container (use an inner minmax(0,1fr) grid row for scrolling). */
    bodyFillsViewport = false,
    useStandardCloseButton = false,
    closeDisabled,
    /** When true, only the body is rendered (entity profile owns its chrome). */
    hideHeader = false,
    contentWrapper,
}: {
    headerLeft: ReactNode;
    /** @deprecated Pass `headerActions` and `useStandardCloseButton` instead. */
    headerRight?: ReactNode;
    headerActions?: ReactNode;
    body: ReactNode;
    footer?: ReactNode;
    onClose: () => void;
    bodyFillsViewport?: boolean;
    useStandardCloseButton?: boolean;
    closeDisabled?: boolean;
    hideHeader?: boolean;
    contentWrapper?: (content: ReactNode) => ReactNode;
}) {
    const mountedAtRef = useRef(Date.now());
    const preventInitialClickReplay = (event: MouseEvent<HTMLDivElement>) => {
        if (Date.now() - mountedAtRef.current < 500) {
            event.stopPropagation();
            event.preventDefault();
        }
    };
    const resolvedHeaderActions = headerActions ?? headerRight;

    return (
        <Modal
            opened={true}
            onClose={onClose}
            classNames={{
                inner: "p-0! right-0! top-0! bottom-0! max-xs:w-full! xs:w-[30rem]! grid! grid-cols-1!",
                content: "w-full! h-full! max-w-[unset]! max-h-[unset]! rounded-none!",
                body: "h-full! p-0!",
            }}
            withCloseButton={false}
        >
            {wrapPanelContent(
                <div
                    onClickCapture={preventInitialClickReplay}
                    className={
                        hideHeader === true
                            ? "grid h-full min-h-0 min-w-0 w-full grid-cols-1"
                            : footer !== undefined && footer !== null
                              ? "grid h-full min-h-0 min-w-0 w-full grid-cols-1 grid-rows-[max-content_max-content_minmax(0,1fr)_max-content_max-content]"
                              : "grid h-full min-h-0 min-w-0 w-full grid-cols-1 grid-rows-[max-content_max-content_minmax(0,1fr)]"
                    }
                >
                    {hideHeader === true ? null : (
                        <PanelModalHeaderRow
                            title={headerLeft}
                            headerActions={resolvedHeaderActions}
                            onCloseHeader={onClose}
                            closeDisabled={closeDisabled}
                            useStandardCloseButton={useStandardCloseButton}
                        />
                    )}

                    <div className="relative h-full min-h-0 min-w-0 w-full">
                        <div
                            className={
                                bodyFillsViewport === true || hideHeader === true
                                    ? "absolute inset-0 min-h-0 min-w-0 overflow-x-hidden overflow-y-hidden"
                                    : "absolute inset-0 min-w-0 overflow-x-hidden overflow-y-auto"
                            }
                        >
                            {body}
                        </div>
                    </div>

                    {hideHeader !== true && footer !== undefined && footer !== null ? (
                        <RowEngagementPanelFooterSlot>{footer}</RowEngagementPanelFooterSlot>
                    ) : null}
                </div>,
                contentWrapper,
            )}
        </Modal>
    );
}

export function FullscreenModal({
    headerLeft,
    headerRight,
    headerActions,
    body,
    footer,
    onClose,
    bodyFillsViewport = false,
    useStandardCloseButton = false,
    closeDisabled,
    contentWrapper,
}: {
    headerLeft: ReactNode;
    /** @deprecated Pass `headerActions` and `useStandardCloseButton` instead. */
    headerRight?: ReactNode;
    headerActions?: ReactNode;
    body: ReactNode;
    footer?: ReactNode;
    onClose: () => void;
    bodyFillsViewport?: boolean;
    useStandardCloseButton?: boolean;
    closeDisabled?: boolean;
    contentWrapper?: (content: ReactNode) => ReactNode;
}) {
    const resolvedHeaderActions = headerActions ?? headerRight;

    return (
        <Modal
            opened={true}
            onClose={onClose}
            classNames={{
                inner: "p-0! right-0! left-0! top-0! bottom-0! grid! grid-cols-1!",
                content: "w-full! h-full! max-w-[unset]! max-h-[unset]! rounded-none!",
                body: "h-full! p-0!",
            }}
            withCloseButton={false}
        >
            {wrapPanelContent(
                <div
                    className={
                        footer !== undefined && footer !== null
                            ? "grid h-full min-h-0 min-w-0 w-full grid-cols-1 grid-rows-[max-content_max-content_minmax(0,1fr)_max-content_max-content]"
                            : "grid h-full min-h-0 min-w-0 w-full grid-cols-1 grid-rows-[max-content_max-content_minmax(0,1fr)]"
                    }
                >
                    <PanelModalHeaderRow
                        title={headerLeft}
                        headerActions={resolvedHeaderActions}
                        onCloseHeader={onClose}
                        closeDisabled={closeDisabled}
                        useStandardCloseButton={useStandardCloseButton}
                    />

                    <div className="relative h-full min-h-0 min-w-0 w-full">
                        <div
                            className={
                                bodyFillsViewport === true
                                    ? "absolute inset-0 min-h-0 min-w-0 overflow-x-hidden overflow-y-hidden"
                                    : "absolute inset-0 min-w-0 overflow-x-hidden overflow-y-auto"
                            }
                        >
                            {body}
                        </div>
                    </div>

                    {footer !== undefined && footer !== null && (
                        <RowEngagementPanelFooterSlot>{footer}</RowEngagementPanelFooterSlot>
                    )}
                </div>,
                contentWrapper,
            )}
        </Modal>
    );
}

export function CreateFullscreenModal({
    headerLeft,
    body,
    breadCrumbItems,
    onClose,
    closeDisabled,
}: {
    headerLeft: string;
    breadCrumbItems?: {
        displayName: string;
        to: string | null;
    }[];
    body: ReactNode;
    onClose: () => void;
    closeDisabled?: boolean;
}) {
    return (
        <Modal
            opened={true}
            onClose={onClose}
            classNames={{
                inner: "p-0! right-0! left-0! top-0! bottom-0! grid! grid-cols-1!",
                content: "w-full! h-full! max-w-[unset]! max-h-[unset]! rounded-none!",
                body: "h-full! p-0!",
            }}
            withCloseButton={false}
        >
            <div className={"w-full h-full grid grid-cols-1 grid-rows-[max-content_minmax(0,1fr)]"}>
                <div className="w-full pt-ds-space-200 pb-ds-space-300 px-ds-space-100 md:px-ds-space-300">
                    {breadCrumbItems !== undefined && (
                        <>
                            <Breadcrumbs items={breadCrumbItems} />
                            <VerticalSpacer className="h-ds-space-025" />
                        </>
                    )}
                    <StandardModalHeader
                        title={<AnankeTempPageTitle title={headerLeft} />}
                        onClose={onClose}
                        closeDisabled={closeDisabled}
                        className="grid w-full min-w-0 grid-cols-[minmax(0,1fr)_max-content] items-center gap-ds-space-400 px-0 py-0"
                    />
                </div>
                <div className="relative h-full min-h-0 min-w-0 w-full">
                    <div className="absolute inset-0 min-w-0 overflow-x-hidden overflow-y-auto">{body}</div>
                </div>
            </div>
        </Modal>
    );
}
